import type { Observation } from '@medplum/fhirtypes';
import React, { useState } from 'react';

import {
  Button,
  Icon,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Stack,
  Tag,
  Text,
  useToast,
} from '@eluve/components';
import { useFhirRestRead } from '@eluve/fhir-data';
import {
  FhirVersionHistory,
  formatChoiceValue,
  formatFhirDate,
  getChoiceValue,
  observationDateText,
} from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';

import { type ReviewDecision, reviewObservation } from '../data/observations';
import { type ObservationGroup, formatMeasureValue } from '../measures';
import {
  isPendingReview,
  readConfidence,
  readEvidence,
} from '../review-markers';

import { TrendChart } from './TrendChart';

/**
 * Deep-linkable side drawer for one observation group (ENG-5373 for
 * observations): full value history with a labeled trend chart, a proposal
 * card per still-pending AI draft (accept / correct / dismiss — the same
 * transitions the backend review capability applies), the recorded-values
 * table, and the latest resource's `_history` audit trail (REST — the FHIR
 * GraphQL spec has no `_history`).
 */
export type ObservationDetailDrawerProps = {
  group: ObservationGroup | undefined;
  open: boolean;
  onClose: () => void;
  /** Called after a review decision lands so the owner can refetch reads. */
  onReviewed: () => void;
};

function observationValueText(observation: Observation): string {
  const quantityValue = observation.valueQuantity?.value;
  if (typeof quantityValue === 'number') {
    // Route quantities through the measure formatter so UCUM annotation
    // units like {score} render as the bare number.
    return formatMeasureValue(
      quantityValue,
      observation.valueQuantity?.unit ?? observation.valueQuantity?.code,
    );
  }
  const choice = getChoiceValue(observation, 'value');
  return choice && choice.type !== 'absent' ? formatChoiceValue(choice) : '—';
}

function confidenceTag(observation: Observation): React.ReactNode {
  const confidence = readConfidence(observation);
  if (confidence === null) return null;
  const label =
    confidence >= 0.75 ? 'High' : confidence >= 0.5 ? 'Medium' : 'Low';
  return (
    <Tag
      pill
      subtle
      size="xs"
      color={confidence >= 0.75 ? 'positive' : 'warning'}
      label={`${label} confidence`}
    />
  );
}

const ProposalCard: React.FC<{
  observation: Observation;
  unit: string | undefined;
  onDecide: (decision: ReviewDecision) => Promise<void>;
  busy: boolean;
  canReview: boolean;
}> = ({ observation, unit, onDecide, busy, canReview }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const evidence = readEvidence(observation);
  const date = observationDateText(observation);
  const method =
    observation.method?.text ?? observation.method?.coding?.[0]?.display;

  const saveAmended = async () => {
    const parsed = Number.parseFloat(editValue);
    if (!Number.isFinite(parsed)) return;
    await onDecide({ kind: 'accept', amendedValue: parsed });
  };

  return (
    <div
      data-testid="observation-proposal-card"
      className="rounded-lg border border-border-insights-subtle bg-bg-insights-subtle p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
        <Icon name="Sparkles" size="xs" className="text-text-insights" />
        <Text inline size="sm" weight="semibold" className="text-text-insights">
          Eluve proposed a new value
        </Text>
        <span className="ml-auto">{confidenceTag(observation)}</span>
      </Stack>

      {editing ? (
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          fullWidth={false}
          className="mt-2"
        >
          <Input
            value={editValue}
            onChange={(event) => setEditValue(event.target.value)}
            inputMode="decimal"
            autoFocus
            aria-label="Corrected value"
            className="w-24 bg-bg-primary font-semibold tabular-nums privacy-text"
          />
          {unit && !unit.startsWith('{') && (
            <Text inline size="sm" color="tertiary">
              {unit}
            </Text>
          )}
        </Stack>
      ) : (
        <Text
          size="md"
          weight="semibold"
          className="mt-1 tabular-nums privacy-text"
        >
          {observationValueText(observation)} proposed
        </Text>
      )}

      <Text
        size="xs"
        color="tertiary"
        weight="regular"
        className="mt-1 privacy-text"
      >
        {[date ? `Source · ${date}` : undefined, method]
          .filter(Boolean)
          .join(' · ')}
      </Text>

      {evidence && (
        <blockquote className="mt-2 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm italic privacy-text">
          “{evidence}”
        </blockquote>
      )}

      <Stack direction="horizontal" gap={2} align="center" className="mt-3">
        {editing ? (
          <>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              disabled={busy || !canReview}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={
                busy ||
                !canReview ||
                !Number.isFinite(Number.parseFloat(editValue))
              }
              onClick={() => void saveAmended()}
              data-testid="proposal-save-amended"
            >
              Save & validate
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy || !canReview}
              onClick={() => void onDecide({ kind: 'dismiss' })}
              data-testid="proposal-dismiss"
            >
              Dismiss
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !canReview}
              onClick={() => {
                setEditValue(String(observation.valueQuantity?.value ?? ''));
                setEditing(true);
              }}
              data-testid="proposal-edit"
            >
              Edit value
            </Button>
            <Button
              size="sm"
              disabled={busy || !canReview}
              onClick={() => void onDecide({ kind: 'accept' })}
              data-testid="proposal-accept"
            >
              Accept
            </Button>
          </>
        )}
      </Stack>
    </div>
  );
};

const ValuesTable: React.FC<{ group: ObservationGroup }> = ({ group }) => {
  const rows = [...group.observations].reverse();
  return (
    <div>
      <Text
        size="xs"
        color="tertiary"
        weight="medium"
        className="tracking-wide uppercase"
      >
        All recorded values
      </Text>
      <div data-testid="observation-values-table">
        {rows.map((observation, index) => {
          const pending = isPendingReview(observation);
          return (
            <div
              key={observation.id ?? index}
              className="flex items-center gap-3 py-2.5 not-first:border-t not-first:border-border-primary"
            >
              <Text
                inline
                size="xs"
                color="tertiary"
                className="w-24 shrink-0 tabular-nums privacy-text"
              >
                {observationDateText(observation) ?? '—'}
              </Text>
              <Text
                inline
                size="sm"
                weight="semibold"
                className="w-20 shrink-0 tabular-nums privacy-text"
              >
                {observationValueText(observation)}
              </Text>
              <Text
                inline
                size="xs"
                color="tertiary"
                className="min-w-0 flex-1 truncate privacy-text"
              >
                {pending
                  ? 'Eluve'
                  : (observation.performer?.[0]?.display ?? '—')}
              </Text>
              {pending && (
                <Tag pill subtle size="xs" color="insights" label="Draft" />
              )}
              {observation.status === 'amended' && (
                <Tag pill subtle size="xs" color="warning" label="Amended" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ObservationDetailDrawer: React.FC<
  ObservationDetailDrawerProps
> = ({ group, open, onClose, onReviewed }) => {
  const { client, practitionerId } = useMedplum();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const latestId = group?.latest.id;
  const latestVersion = group?.latest.meta?.versionId;
  const history = useFhirRestRead(
    latestId ? (medplum) => medplum.readHistory('Observation', latestId) : null,
    [latestId, latestVersion],
  );
  const versions: Observation[] = (history.data?.entry ?? []).flatMap(
    (entry) => (entry.resource ? [entry.resource] : []),
  );

  const decide = async (observation: Observation, decision: ReviewDecision) => {
    if (!client || !observation.id) return;
    setBusy(true);
    try {
      await reviewObservation(client, {
        observationId: observation.id,
        decision,
        reviewerReference: practitionerId
          ? `Practitioner/${practitionerId}`
          : null,
      });
      toast.success(
        decision.kind === 'dismiss' ? 'Draft dismissed.' : 'Value validated.',
      );
      onReviewed();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not save the review decision.',
      );
    } finally {
      setBusy(false);
    }
  };

  const pendingNewestFirst = group ? [...group.pending].reverse() : [];
  const latestValidated = group?.series[group.series.length - 1];
  const latestPending = pendingNewestFirst[0];
  const proposedPoint =
    latestPending?.valueQuantity?.value !== undefined
      ? {
          value: latestPending.valueQuantity.value,
          dateLabel:
            formatFhirDate(
              latestPending.effectiveDateTime ?? latestPending.issued,
            ) ?? 'Proposed',
        }
      : undefined;

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-[min(640px,94vw)] gap-0 overflow-y-auto sm:max-w-none"
        data-testid="observation-detail-drawer"
      >
        {group && (
          <>
            <SheetHeader>
              <SheetTitle className="privacy-text">{group.label}</SheetTitle>
              <SheetDescription className="privacy-text">
                {[
                  latestValidated
                    ? `Current ${observationValueText(
                        latestValidated.observation,
                      )}`
                    : undefined,
                  group.method,
                  `${group.observations.length} recorded ${
                    group.observations.length === 1 ? 'value' : 'values'
                  }`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </SheetDescription>
            </SheetHeader>

            <Stack gap={6} align="stretch" className="px-4 pb-6">
              <TrendChart
                points={group.series.map((point) => ({
                  value: point.value,
                  dateLabel: observationDateText(point.observation) ?? '',
                  amended: point.amended,
                }))}
                proposed={proposedPoint}
                unit={
                  group.unit && !group.unit.startsWith('{')
                    ? group.unit
                    : undefined
                }
              />

              {pendingNewestFirst.map((observation) => (
                <ProposalCard
                  key={observation.id}
                  observation={observation}
                  unit={group.unit}
                  busy={busy}
                  canReview={Boolean(client)}
                  onDecide={(decision) => decide(observation, decision)}
                />
              ))}

              <ValuesTable group={group} />

              <div>
                <Text
                  size="xs"
                  color="tertiary"
                  weight="medium"
                  className="mb-2 tracking-wide uppercase"
                >
                  Change log & audit trail
                </Text>
                {history.loading ? (
                  <Text size="sm" color="tertiary" weight="regular">
                    Loading history…
                  </Text>
                ) : (
                  <FhirVersionHistory versions={versions} />
                )}
              </div>
            </Stack>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
